/**
 * Transactional inline edits: validate the whole plan, then mutate, or do not
 * touch the tree at all.
 *
 * Marker removal, emoji replacement and seam repair frequently span several
 * leaves of one block. Applying them one at a time leaves a half-typeset node
 * behind on the first fault, which the plain-path re-render then inherits.
 * Every rejection is decided before the first assignment.
 */
import {
    classifyEditorialCapabilities,
    type EditorialContract,
    type EditorialHastNode,
} from "./editorial-capabilities";
import {
    isDisposableWrapper,
    resolveContainerOutcome,
    type EditorialContainerOutcome,
    type EditorialContainerState,
} from "./editorial-container-outcomes";
import { buildInlineRun } from "./editorial-inline-run";

type HastNodeLike = EditorialHastNode;

export interface EditorialReplaceSplice {
    readonly kind: "replace";
    readonly leaf: HastNodeLike;
    readonly start: number;
    readonly end: number;
    readonly nodes: readonly HastNodeLike[];
}

export interface EditorialDeleteSplice {
    readonly kind: "delete";
    readonly leaf: HastNodeLike;
    readonly start: number;
    readonly end: number;
}

/**
 * An anchor position rather than a range. `offset === leaf.length` is valid and
 * is the only route to end-of-run insertion: the cursor's `resolve` returns
 * undefined there, so that position can never be discovered by walking offsets.
 */
export interface EditorialInsertSplice {
    readonly kind: "insert";
    readonly leaf: HastNodeLike;
    readonly offset: number;
    readonly nodes: readonly HastNodeLike[];
}

export type EditorialSplice =
    | EditorialReplaceSplice
    | EditorialDeleteSplice
    | EditorialInsertSplice;

export interface EditorialEditPlan {
    readonly splices: readonly EditorialSplice[];
}

export interface EditorialEditContext {
    readonly block: HastNodeLike;
}

export interface EditorialEditPlanReport {
    readonly valid: boolean;
    readonly errors: readonly string[];
    /**
     * What becomes of the block if this plan is applied. Present only once the
     * splices themselves are sound, because the projected content of an
     * out-of-bounds plan means nothing.
     *
     * Reported rather than kept private: the pass that acts on outcomes needs
     * this exact value, and recomputing it from the mutated tree is both a
     * second implementation and a different question — after apply, the
     * evidence of what was removed is gone.
     */
    readonly containerOutcome?: EditorialContainerOutcome;
}

interface NormalizedSplice {
    readonly splice: EditorialSplice;
    readonly start: number;
    readonly end: number;
}

// An insert collapses to a zero-width range, which makes the ordinary
// half-open overlap test produce exactly the right answers for free: an insert
// inside a deleted range conflicts, one against either edge does not, and two
// inserts at the same point coexist in plan order.
const normalize = (splice: EditorialSplice): NormalizedSplice =>
    splice.kind === "insert"
        ? { splice, start: splice.offset, end: splice.offset }
        : { splice, start: splice.start, end: splice.end };

const insertedNodes = (
    splice: EditorialSplice
): readonly HastNodeLike[] | undefined =>
    splice.kind === "delete" ? undefined : splice.nodes;

const isInSubtree = (root: HastNodeLike, target: HastNodeLike): boolean => {
    const stack = [root];
    const seen = new Set<unknown>();
    while (stack.length > 0) {
        const node = stack.pop() as HastNodeLike;
        if (node === target) return true;
        if (seen.has(node)) continue;
        seen.add(node);
        for (const child of node.children ?? []) stack.push(child);
    }
    return false;
};

const overlaps = (a: NormalizedSplice, b: NormalizedSplice): boolean =>
    a.start < b.end && b.start < a.end;

/**
 * Roots this layer refuses outright.
 *
 * The projection exempts its root on purpose — the caller names the block, and
 * a table cell is block-scan opaque while still receiving inline typesetting
 * through the adapters. That exemption is what makes the root the one node no
 * boundary check covered: leaves inside a `code` are unreachable, but the same
 * `code` handed over as the block projects its whole subtree.
 *
 * `source-model` is absent because cells are exactly the case the exemption
 * exists for, and `interactive` because an anchor as block is the
 * transactional label path — emptying it is already refused by the
 * container-outcome clause below.
 */
const UNEDITABLE_ROOT_CONTRACTS: ReadonlySet<EditorialContract> = new Set([
    "verbatim",
    "protocol",
    "generated",
]);

const groupByLeaf = (
    normalized: readonly NormalizedSplice[]
): Map<HastNodeLike, NormalizedSplice[]> => {
    const byLeaf = new Map<HastNodeLike, NormalizedSplice[]>();
    for (const entry of normalized) {
        const existing = byLeaf.get(entry.splice.leaf);
        if (existing) existing.push(entry);
        else byLeaf.set(entry.splice.leaf, [entry]);
    }
    return byLeaf;
};

/**
 * Whether a node would still render anything once the plan is applied.
 *
 * Asked of the tree AS IT WILL BE, not as it is: a `<strong>` whose only text
 * this plan consumes still has a child right now, and reading it in the
 * present tense reports a paragraph as substantive when it is about to render
 * nothing at all.
 */
const rendersAfterPlan = (
    node: HastNodeLike,
    byLeaf: ReadonlyMap<HastNodeLike, NormalizedSplice[]>
): boolean => {
    const stack = [node];
    const seen = new Set<unknown>();
    while (stack.length > 0) {
        const current = stack.pop() as HastNodeLike;
        if (seen.has(current)) continue;
        seen.add(current);

        if (current.type === "text") {
            const spans = byLeaf.get(current);
            if (!spans && (current.value ?? "") !== "") return true;
            if (
                spans &&
                expandLeaf(current, spans).some(
                    (produced) =>
                        produced.type !== "text" ||
                        (produced.value ?? "") !== ""
                )
            ) {
                return true;
            }
            continue;
        }
        if (current.type !== "element") continue;

        const children = current.children ?? [];
        if (children.length === 0) {
            if (!isDisposableWrapper(current)) return true;
            continue;
        }
        for (const child of children) stack.push(child);
    }
    return false;
};

/**
 * What this plan DOES to the block, without applying it.
 *
 * Measured as a delta rather than an absolute: an outcome answers "what should
 * happen to the container because of this transform", so a container that was
 * already display-empty before the plan is not something the plan emptied.
 * Read absolutely, a plan with no splices at all reported that it emptied its
 * container — recommending the deletion of an untouched paragraph, and
 * refusing an interactive block it had never touched.
 *
 * Reuses `expandLeaf` rather than repeating its range arithmetic, so the
 * projection and the mutation cannot disagree about what survives.
 */
const projectContainerState = (
    block: HastNodeLike,
    leaves: readonly HastNodeLike[],
    normalized: readonly NormalizedSplice[]
): EditorialContainerState => {
    const byLeaf = groupByLeaf(normalized);
    const surviving = leaves
        .map((leaf) => {
            const spans = byLeaf.get(leaf);
            if (!spans) return leaf.value ?? "";
            return expandLeaf(leaf, spans)
                .filter((node) => node.type === "text")
                .map((node) => node.value ?? "")
                .join("");
        })
        .join("");

    const insertsNodes = normalized.some(
        (entry) => (insertedNodes(entry.splice) ?? []).length > 0
    );

    const before = leaves.map((leaf) => leaf.value ?? "").join("");

    return {
        // Conservative in one direction only: a block that keeps rendering
        // through something other than text may read as empty here, and the
        // sole consequence is declining a transform that would have survived.
        displayEmpty:
            surviving.trim() === "" && before.trim() !== "" && !insertsNodes,
        hasSubstantiveChild: (block.children ?? []).some(
            (child) =>
                child.type === "element" && rendersAfterPlan(child, byLeaf)
        ),
    };
};

export const validateEditPlan = (
    plan: EditorialEditPlan,
    context: EditorialEditContext
): EditorialEditPlanReport => {
    const { block } = context;
    const errors: string[] = [];

    const rootContract = classifyEditorialCapabilities(block).contract;
    if (UNEDITABLE_ROOT_CONTRACTS.has(rootContract)) {
        return {
            valid: false,
            errors: [
                `The block is a ${rootContract} boundary, so nothing within it may be edited.`,
            ],
        };
    }

    // The inline run is the single authority on what this block may splice.
    // Restating the descent rule here is the drift bug the shared cursor exists
    // to prevent — a leaf behind an opaque boundary or inside a nested block is
    // simply absent from it.
    const leaves = buildInlineRun(block).leaves;
    const spliceable = new Set(leaves);
    const normalized = plan.splices.map(normalize);

    normalized.forEach((entry, index) => {
        const { splice, start, end } = entry;
        const leaf = splice.leaf;

        if (!spliceable.has(leaf)) {
            errors.push(
                isInSubtree(block, leaf)
                    ? `Splice ${index} targets a leaf behind a boundary the editorial layer may not edit.`
                    : `Splice ${index} targets a leaf that is not attached to the block.`
            );
            return;
        }

        const length = (leaf.value ?? "").length;
        // Checked before any comparison, because every comparison against NaN
        // is false: it would pass the range tests below, pass the overlap test,
        // and then suppress both the leading text and the tail in expandLeaf —
        // deleting the whole leaf under a plan reported as legal.
        if (!Number.isInteger(start) || !Number.isInteger(end)) {
            errors.push(
                `Splice ${index} has a non-integer offset, which is out of bounds for any leaf.`
            );
            return;
        }
        if (start > end) {
            errors.push(`Splice ${index} starts after it ends.`);
            return;
        }
        if (start < 0 || end > length) {
            errors.push(
                `Splice ${index} is out of bounds for its leaf of length ${length}.`
            );
            return;
        }
    });

    if (errors.length > 0) return { valid: false, errors };

    const byLeaf = new Map<
        HastNodeLike,
        Array<NormalizedSplice & { readonly index: number }>
    >();
    normalized.forEach((entry, index) => {
        const group = byLeaf.get(entry.splice.leaf) ?? [];
        group.push({ ...entry, index });
        byLeaf.set(entry.splice.leaf, group);
    });
    for (const group of byLeaf.values()) {
        group.sort(
            (left, right) => left.start - right.start || left.end - right.end
        );
        let active: (NormalizedSplice & { readonly index: number }) | undefined;
        for (const entry of group) {
            if (active && overlaps(active, entry)) {
                errors.push(
                    `Splice ${active.index} overlaps splice ${entry.index} in the same leaf.`
                );
                break;
            }
            if (
                entry.end > entry.start &&
                (!active || entry.end > active.end)
            ) {
                active = entry;
            }
        }
    }

    if (errors.length > 0) return { valid: false, errors };

    // Container outcomes, resolved only once the splices themselves hold. A
    // container the layer may not empty is the one outcome that invalidates an
    // otherwise legal plan — everything else it resolves to is a rendering
    // decision for whoever owns the tree, and is reported rather than acted on.
    const state = projectContainerState(block, leaves, normalized);
    const containerOutcome = resolveContainerOutcome(block, state);
    if (containerOutcome === "rollback") {
        errors.push(
            "The plan empties a container whose outcome is rollback; it must be abandoned rather than applied."
        );
    }

    return { valid: errors.length === 0, errors, containerOutcome };
};

const collectParents = (
    block: HastNodeLike
): Map<HastNodeLike, HastNodeLike> => {
    const parents = new Map<HastNodeLike, HastNodeLike>();
    const stack = [block];
    const seen = new Set<unknown>();
    while (stack.length > 0) {
        const node = stack.pop() as HastNodeLike;
        if (seen.has(node)) continue;
        seen.add(node);
        for (const child of node.children ?? []) {
            if (child !== block && !parents.has(child))
                parents.set(child, node);
            stack.push(child);
        }
    }
    return parents;
};

/** Rebuild one leaf as the sequence of nodes its splices leave behind. */
const expandLeaf = (
    leaf: HastNodeLike,
    splices: readonly NormalizedSplice[]
): HastNodeLike[] => {
    const value = leaf.value ?? "";
    // Stable sort, so two splices anchored at the same offset keep plan order.
    const ordered = [...splices].sort((a, b) => a.start - b.start);
    const out: HastNodeLike[] = [];
    let cursor = 0;

    for (const entry of ordered) {
        if (entry.start > cursor) {
            out.push({ type: "text", value: value.slice(cursor, entry.start) });
        }
        out.push(...(insertedNodes(entry.splice) ?? []));
        // Never backwards. A zero-width insert sharing its offset with a range
        // splice sorts equal, so plan order can put it second — and assigning
        // its `end` would rewind the cursor into text the range already
        // consumed, re-emitting it in the tail.
        cursor = Math.max(cursor, entry.end);
    }
    // A surviving tail, and never a zero-length text node: an empty leaf is
    // unaddressable by the cursor, so emitting one would be invisible debris.
    if (cursor < value.length) {
        out.push({ type: "text", value: value.slice(cursor) });
    }
    return out;
};

/**
 * `validated` is the report the caller already computed for THIS plan and
 * block. Validation is not cheap — it rebuilds the inline run and projects the
 * container state — and the one production caller has to validate first anyway,
 * because the container outcome decides whether to apply at all. Passing the
 * report it already holds spares a second full pass per typeset block on every
 * streaming frame. Omit it and the plan is checked here, which is what every
 * caller arriving with an unchecked plan wants.
 */
export const applyEditPlan = (
    plan: EditorialEditPlan,
    context: EditorialEditContext,
    validated?: EditorialEditPlanReport
): void => {
    const report = validated ?? validateEditPlan(plan, context);
    if (!report.valid) {
        throw new Error(
            `Editorial edit plan rejected: ${report.errors.join(" ")}`
        );
    }

    const byLeaf = groupByLeaf(plan.splices.map(normalize));
    if (byLeaf.size === 0) return;

    const parents = collectParents(context.block);
    const affectedParents = new Set<HastNodeLike>();
    for (const leaf of byLeaf.keys()) {
        const parent = parents.get(leaf);
        if (parent) affectedParents.add(parent);
    }

    // One assignment per parent rather than one per splice: each write is a new
    // array identity, and the markdown memo comparator treats that as a change.
    for (const parent of affectedParents) {
        const next: HastNodeLike[] = [];
        for (const child of parent.children ?? []) {
            const splices = byLeaf.get(child);
            if (splices) next.push(...expandLeaf(child, splices));
            else next.push(child);
        }
        parent.children = next;
    }
};
